import React, { forwardRef } from 'react';
import {
  extend,
  params,
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
} from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from '@one-thd/sui-atomic-components';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useSearchModelData } from '../hooks/useSearchModelData';
import '../styles/results-wrapped.scss';

const { bool, string } = PropTypes;

const ResultsWrapped = forwardRef(({ children = null }, ref) => {
  const { data, loading } = useSearchModelData();
  const theme = useTheme(ResultsWrapped) || {};
  const { showHomePageButton, noProductResultsMessage } = theme.props;

  const wrappedClasses = classNames('results-wrapped', {
    'results-wrapped--fade': (loading && data)
  });
  const displayMessage = !loading
    && data
    && data?.searchModel?.metadata
    && !data?.searchModel?.metadata.searchRedirect
    && data?.searchModel?.products?.length === 0;

  return (
    <div className={wrappedClasses} data-component="ResultsWrapped" ref={ref}>
      {displayMessage ? (
        <div className="results-wrapped--no-products">
          {noProductResultsMessage}
          {showHomePageButton && (
            <Button variant="primary" onClick={() => { window.location.href = '/'; }}>
              Return to Home Page
            </Button>
          )}
        </div>
      ) : children}
    </div>
  );
});

ResultsWrapped.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};

ResultsWrapped.dataModel = extend({
  searchModel: params({
    keyword: stringType(),
    navParam: stringType(),
    storeId: stringType(),
  }).shape({
    products: arrayType(shapeType({
      itemId: stringType()
    })),
    metadata: shapeType({
      canonicalUrl: stringType(),
      clearAllRefinementsURL: stringType(),
      searchRedirect: stringType(),
      contentType: stringType(),
      h1Tag: stringType(),
      isStoreDisplay: stringType(),
      productCount: shapeType({
        inStore: stringType(),
      }),
      stores: shapeType({
        storeId: stringType(),
        storeName: stringType(),
        address: shapeType({
          postalCode: stringType()
        }),
        nearByStores: arrayType(shapeType({
          storeId: stringType(),
          storeName: stringType()
        }))
      }),
    }),
  }),
});

ResultsWrapped.defaultProps = {
  children: null
};

ResultsWrapped.displayName = 'ResultsWrapped';

ResultsWrapped.themeProps = {
  showHomePageButton: bool,
  noProductResultsMessage: string
};

ResultsWrapped.defaultThemeProps = {
  showHomepageButton: false,
  noProductResultsMessage: 'Sorry, there are no products available online or in your local store. '
    + 'Please use our Store Finder to select another local store.'
};

export { ResultsWrapped };
